// Unified Data Service - Single Source of Truth for all GTM Dashboard data
// Replaces direct usage of realDataService and manages leadsStore automatically

import realDataService from './realDataService';
import { leadsStore, type Lead } from '../stores/leadsStore';

export interface DataServiceConfig {
  leadCount: number;
  cacheDuration: number;
  autoRefresh: boolean;
  refreshInterval?: number;
}

export interface DataMetrics {
  totalLeads: number;
  lastUpdated: string;
  cacheStatus: 'fresh' | 'stale' | 'empty';
  averageValue: number;
  statusBreakdown: Record<Lead['status'], number>;
}

class UnifiedDataService {
  private config: DataServiceConfig = {
    leadCount: 25,
    cacheDuration: 5 * 60 * 1000, // 5 minutes
    autoRefresh: false
  };

  private isLoading = false;
  private lastLoadTime = 0;
  private refreshTimer?: NodeJS.Timeout;
  private listeners: Set<(leads: Lead[]) => void> = new Set();

  /**
   * Configure the data service
   */
  configure(config: Partial<DataServiceConfig>): void {
    this.config = { ...this.config, ...config };

    // Setup auto-refresh if enabled
    if (config.autoRefresh && config.refreshInterval) {
      this.setupAutoRefresh(config.refreshInterval);
    } else if (!config.autoRefresh) {
      this.clearAutoRefresh();
    }
  }

  /**
   * Get current leads (primary method for all consumers)
   */
  async getLeads(): Promise<Lead[]> {
    // If we have fresh data in the store, return it
    if (this.hasFreshData()) {
      return leadsStore.getLeads();
    }

    // Otherwise, load fresh data
    return this.refreshLeads();
  }

  /**
   * Refresh leads by adding new ones while preserving existing historical data
   */
  async refreshLeads(): Promise<Lead[]> {
    if (this.isLoading) {
      // If already loading, wait for completion
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!this.isLoading) {
            resolve(leadsStore.getLeads());
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }

    this.isLoading = true;

    try {
      const existingLeads = leadsStore.getLeads();
      console.log(`[UnifiedDataService] Current database: ${existingLeads.length} leads`);

      // If we have no leads, do initial load
      if (existingLeads.length === 0) {
        console.log(`[UnifiedDataService] Initial load: generating ${this.config.leadCount} leads...`);
        realDataService.clearCache();
        const initialLeads = await realDataService.generateRealLeads(this.config.leadCount);
        leadsStore.setLeads(initialLeads);
        this.lastLoadTime = Date.now();
        this.notifyListeners(initialLeads);
        console.log(`[UnifiedDataService] Initial load complete: ${initialLeads.length} leads`);
        return initialLeads;
      }

      // For existing database, just update timestamps and add occasional new leads
      console.log(`[UnifiedDataService] Preserving existing ${existingLeads.length} leads, updating activity...`);

      // Update last activity dates for some existing leads to simulate ongoing business
      const updatedLeads = existingLeads.map(lead => {
        if (Math.random() < 0.3) { // 30% chance to update activity
          return {
            ...lead,
            lastActivityDate: new Date().toISOString().split('T')[0]
          };
        }
        return lead;
      });

      // Occasionally add 1-2 new leads to grow the database
      if (Math.random() < 0.4) { // 40% chance to add new leads
        console.log(`[UnifiedDataService] Adding new leads to historical database...`);
        const newLeadsCount = Math.floor(Math.random() * 2) + 1; // 1-2 new leads
        const newLeads = await realDataService.generateRealLeads(newLeadsCount);

        // Add new leads with unique IDs
        const uniqueNewLeads = newLeads.map((lead, index) => ({
          ...lead,
          id: `historical-${Date.now()}-${index}`,
          createdAt: new Date().toISOString().split('T')[0]
        }));

        updatedLeads.push(...uniqueNewLeads);
        console.log(`[UnifiedDataService] Added ${uniqueNewLeads.length} new leads`);
      }

      leadsStore.setLeads(updatedLeads);
      this.lastLoadTime = Date.now();
      this.notifyListeners(updatedLeads);

      console.log(`[UnifiedDataService] Historical database maintained: ${updatedLeads.length} total leads`);
      console.log(`[UnifiedDataService] Sample leads:`, updatedLeads.slice(0, 3).map(l => l.name));

      return updatedLeads;

    } catch (error) {
      console.error('[UnifiedDataService] Failed to refresh leads:', error);
      // Return existing data if available, empty array otherwise
      return leadsStore.getLeads();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get current leads synchronously (for components that need immediate access)
   */
  getCurrentLeads(): Lead[] {
    return leadsStore.getLeads();
  }

  /**
   * Get data metrics and status
   */
  getMetrics(): DataMetrics {
    const leads = leadsStore.getLeads();
    const now = Date.now();
    const timeSinceUpdate = now - this.lastLoadTime;

    // Calculate status breakdown
    const statusBreakdown = leads.reduce((acc, lead) => {
      acc[lead.status] = (acc[lead.status] || 0) + 1;
      return acc;
    }, {} as Record<Lead['status'], number>);

    return {
      totalLeads: leads.length,
      lastUpdated: new Date(this.lastLoadTime).toISOString(),
      cacheStatus: timeSinceUpdate > this.config.cacheDuration ? 'stale' :
                   leads.length === 0 ? 'empty' : 'fresh',
      averageValue: leads.length > 0 ?
        Math.round(leads.reduce((sum, lead) => sum + lead.value, 0) / leads.length) : 0,
      statusBreakdown
    };
  }

  /**
   * Subscribe to data changes
   */
  subscribe(listener: (leads: Lead[]) => void): () => void {
    this.listeners.add(listener);

    // Immediately notify with current data
    listener(leadsStore.getLeads());

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Add a new lead
   */
  async addLead(lead: Omit<Lead, 'id' | 'createdAt'>): Promise<Lead> {
    const newLead: Lead = {
      ...lead,
      id: `custom-${Date.now()}`,
      createdAt: new Date().toISOString().split('T')[0]
    };

    leadsStore.addLead(newLead);
    this.notifyListeners(leadsStore.getLeads());

    return newLead;
  }

  /**
   * Update a lead
   */
  async updateLead(id: string, updates: Partial<Lead>): Promise<void> {
    leadsStore.updateLead(id, updates);
    this.notifyListeners(leadsStore.getLeads());
  }

  /**
   * Delete a lead
   */
  async deleteLead(id: string): Promise<void> {
    leadsStore.deleteLead(id);
    this.notifyListeners(leadsStore.getLeads());
  }

  /**
   * Search leads
   */
  searchLeads(query: string): Lead[] {
    if (!query.trim()) return leadsStore.getLeads();

    const searchTerm = query.toLowerCase();
    return leadsStore.getLeads().filter(lead =>
      lead.name.toLowerCase().includes(searchTerm) ||
      lead.company.toLowerCase().includes(searchTerm) ||
      lead.email.toLowerCase().includes(searchTerm) ||
      lead.status.toLowerCase().includes(searchTerm)
    );
  }

  /**
   * Filter leads by criteria
   */
  filterLeads(filters: {
    status?: Lead['status'][];
    minValue?: number;
    maxValue?: number;
    source?: string;
  }): Lead[] {
    let leads = leadsStore.getLeads();

    if (filters.status && filters.status.length > 0) {
      leads = leads.filter(lead => filters.status!.includes(lead.status));
    }

    if (filters.minValue !== undefined) {
      leads = leads.filter(lead => lead.value >= filters.minValue!);
    }

    if (filters.maxValue !== undefined) {
      leads = leads.filter(lead => lead.value <= filters.maxValue!);
    }

    if (filters.source) {
      leads = leads.filter(lead => lead.source === filters.source);
    }

    return leads;
  }

  /**
   * Get high-value leads
   */
  getHighValueLeads(minValue: number = 50000): Lead[] {
    return leadsStore.getHighValueLeads(minValue);
  }

  /**
   * Get activity feed data
   */
  async getActivityFeed(count: number = 10): Promise<any[]> {
    try {
      return await realDataService.generateActivityFeed(count);
    } catch (error) {
      console.error('[UnifiedDataService] Failed to load activity feed:', error);
      return [];
    }
  }

  // Private methods
  private hasFreshData(): boolean {
    const leads = leadsStore.getLeads();
    if (leads.length === 0) return false;

    const timeSinceUpdate = Date.now() - this.lastLoadTime;
    return timeSinceUpdate < this.config.cacheDuration;
  }

  private setupAutoRefresh(interval: number): void {
    this.clearAutoRefresh();
    this.refreshTimer = setInterval(() => {
      this.refreshLeads().catch(error => {
        console.error('[UnifiedDataService] Auto-refresh failed:', error);
      });
    }, interval);
  }

  private clearAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  private notifyListeners(leads: Lead[]): void {
    this.listeners.forEach(listener => {
      try {
        listener(leads);
      } catch (error) {
        console.error('[UnifiedDataService] Listener error:', error);
      }
    });
  }

  /**
   * Cleanup method
   */
  destroy(): void {
    this.clearAutoRefresh();
    this.listeners.clear();
  }
}

// Export singleton instance
export const unifiedDataService = new UnifiedDataService();
export default unifiedDataService;